import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const WeeklyReportsPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState('');

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('informes_loncheado_semanales')
      .select('*, cliente:clientes(nombre)')
      .order('semana_inicio', { ascending: false })
      .order('cliente_id');
    if (!error) setRows(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const weeks = useMemo(() => [...new Set(rows.map(r => r.semana_inicio))], [rows]);
  const [week, setWeek] = useState('');
  useEffect(() => { if (!week && weeks.length) setWeek(weeks[0]); }, [weeks, week]);

  const shown = rows.filter(r => !week || r.semana_inicio === week);
  const weekMeta = shown[0];
  const generated = shown.filter(r => r.estado === 'GENERADO' && r.archivo_path);
  const totalPieces = shown.reduce((s, r) => s + Number(r.numero_piezas || 0), 0);

  const signedUrl = async (r, expires = 120) => {
    const { data, error } = await supabase.storage.from('informes-loncheado').createSignedUrl(r.archivo_path, expires);
    if (error) throw error;
    return data.signedUrl;
  };

  const download = async (r) => {
    if (!r.archivo_path) return;
    try {
      const url = await signedUrl(r);
      const a = document.createElement('a');
      a.href = url;
      a.download = r.archivo_path.split('/').pop();
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      setMessage('No se pudo descargar el informe.');
    }
  };

  const invokeReportGenerator = async (body) => {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;
    if (!accessToken) throw new Error('Sesión no disponible');

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const response = await fetch(`${supabaseUrl}/functions/v1/generar-informes-loncheado-semanales`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: anonKey,
        'Content-Type': 'text/plain'
      },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error(await response.text());
    return response.json();
  };

  const regenerate = async (r) => {
    setBusy(r.id);
    setMessage('');
    try {
      await invokeReportGenerator({ weekStart: r.semana_inicio, clientId: r.cliente_id, regenerate: true });
      setMessage('Informe regenerado correctamente.');
    } catch (e) {
      setMessage('Error al regenerar el informe.');
    }
    await load();
    setBusy(null);
  };

  const regenerateWeek = async () => {
    if (!week) return;
    setBusy('week');
    setMessage('');
    try {
      await invokeReportGenerator({ weekStart: week, regenerate: true });
      setMessage('Semana regenerada correctamente.');
    } catch (e) {
      setMessage('Error al regenerar la semana.');
    }
    await load();
    setBusy(null);
  };

  const downloadAll = async () => {
    if (!generated.length) return;
    setMessage('');
    try {
      for (const r of generated) {
        await download(r);
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      setMessage(`${generated.length} informes enviados a descarga.`);
    } catch (e) {
      setMessage('No se pudieron descargar todos los informes.');
    }
  };

  if (loading) {
    return (
      <section className="rounded-xl mb-5 px-5 py-4" style={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,.08)' }}>
        <p className="font-oranienbaum text-sm text-white/50">Cargando informes semanales…</p>
      </section>
    );
  }

  if (!rows.length) {
    return (
      <section className="rounded-xl mb-5 px-5 py-4" style={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,.08)' }}>
        <h2 className="font-oranienbaum text-lg font-bold text-white">Informes semanales</h2>
        <p className="font-oranienbaum text-sm text-white/45 mt-1">Todavía no hay cierres semanales generados.</p>
      </section>
    );
  }

  return (
    <section className="rounded-xl mb-5 overflow-hidden" style={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,.08)' }}>
      <div className="px-5 py-4 flex flex-wrap items-center justify-between gap-3" style={{ borderBottom: '1px solid rgba(255,255,255,.08)' }}>
        <div>
          <h2 className="font-oranienbaum text-lg font-bold text-white">Informes semanales</h2>
          <p className="font-oranienbaum text-xs text-white/40 mt-1">Cierre automático por cliente · producción y trazabilidad</p>
          {weekMeta && <p className="font-oranienbaum text-xs mt-2" style={{ color: '#D6EF4E' }}>{shown.length} clientes · {totalPieces} piezas · {generated.length} informes generados</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={week} onChange={e => setWeek(e.target.value)} className="rounded-lg px-3 py-2 text-sm font-oranienbaum" style={{ background: '#111', color: 'rgba(255,255,255,.8)', border: '1px solid rgba(255,255,255,.1)' }}>
            {weeks.map(w => { const r = rows.find(x => x.semana_inicio === w); return <option key={w} value={w}>{r?.anio} — Semana {r?.numero_semana}</option>; })}
          </select>
          <button disabled={busy === 'week'} onClick={regenerateWeek} className="rounded-lg px-3 py-2 text-sm font-oranienbaum flex items-center gap-2 disabled:opacity-40" style={{ color: 'rgba(255,255,255,.75)', border: '1px solid rgba(255,255,255,.12)' }}>
            <Icon name="RefreshCw" size={14} className={busy === 'week' ? 'animate-spin' : ''}/>{busy === 'week' ? 'Regenerando…' : 'Regenerar semana'}
          </button>
          <button disabled={!generated.length} onClick={downloadAll} className="rounded-lg px-3 py-2 text-sm font-oranienbaum flex items-center gap-2 disabled:opacity-40" style={{ background: 'rgba(214,239,78,.12)', color: '#D6EF4E', border: '1px solid rgba(214,239,78,.25)' }}>
            <Icon name="Download" size={14}/>Descargar todos
          </button>
        </div>
      </div>
      {message && <div className="px-5 py-2 text-xs font-oranienbaum" style={{ color: '#D6EF4E', borderBottom: '1px solid rgba(255,255,255,.06)' }}>{message}</div>}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[850px] font-oranienbaum">
          <thead><tr className="text-left text-sm" style={{ color: '#D6EF4E', background: 'rgba(214,239,78,.06)' }}>
            <th className="px-5 py-3">Cliente</th><th className="px-4 py-3">Piezas</th><th className="px-4 py-3">Rendimiento</th><th className="px-4 py-3">Generado</th><th className="px-4 py-3">Estado</th><th className="px-5 py-3 text-right">Acciones</th>
          </tr></thead>
          <tbody>{shown.map(r => <tr key={r.id} style={{ borderTop: '1px solid rgba(255,255,255,.06)' }} className="text-white/80 text-sm">
            <td className="px-5 py-3 text-base text-white">{r.cliente?.nombre || 'Cliente'}</td>
            <td className="px-4 py-3">{r.numero_piezas}</td>
            <td className="px-4 py-3">{r.rendimiento_medio != null ? Number(r.rendimiento_medio).toFixed(1) + '%' : '—'}</td>
            <td className="px-4 py-3">{r.fecha_generacion ? new Date(r.fecha_generacion).toLocaleString('es-ES') : '—'}</td>
            <td className="px-4 py-3"><span className="rounded-full px-2.5 py-1 text-xs" style={{ background: r.estado === 'GENERADO' ? 'rgba(34,139,86,.16)' : r.estado === 'ERROR' ? 'rgba(224,82,82,.16)' : 'rgba(201,162,77,.16)', color: r.estado === 'GENERADO' ? '#5aab8e' : r.estado === 'ERROR' ? '#E05252' : '#C9A24D' }}>{r.estado}</span></td>
            <td className="px-5 py-3"><div className="flex justify-end gap-2">
              <button disabled={r.estado !== 'GENERADO'} onClick={() => download(r)} className="px-3 py-1.5 rounded-lg text-xs border border-white/10 hover:border-white/20 disabled:opacity-30">Descargar</button>
              <button disabled={busy === r.id} onClick={() => regenerate(r)} className="px-3 py-1.5 rounded-lg text-xs border border-white/10 hover:border-white/20 disabled:opacity-30">{busy === r.id ? 'Generando…' : 'Regenerar'}</button>
            </div></td>
          </tr>)}</tbody>
        </table>
      </div>
    </section>
  );
};

export default WeeklyReportsPanel;
